import type { FlatRate } from '@/components/billing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { ACCESSORY_SUGGESTIONS, formatCents, parseEuro, WARRANTIES } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, Input, Select, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { CustomerDetail, DeviceItem, Paginated, Ref, TicketMeta, WarrantyType } from '@/lib/types';
import { cn } from '@/lib/utils';
import { CustomerDialog } from '../customers/customer-form';

const emptyDevice = { manufacturer: '', deviceType: '', model: '', serialNumber: '', inventoryNumber: '' };

export function NewRepairPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const ticketId = params.get('ticketId');
  const [customer, setCustomer] = useState<Ref | null>(params.get('customerId') ? { id: params.get('customerId')!, name: params.get('customerName') ?? 'Kunde' } : null);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [contactId, setContactId] = useState('');
  const [deviceId, setDeviceId] = useState<string>('new');
  const [device, setDevice] = useState(emptyDevice);
  const [fault, setFault] = useState(params.get('fault') ?? '');
  const [condition, setCondition] = useState('');
  const [accessories, setAccessories] = useState<string[]>([]);
  const [accessoryInput, setAccessoryInput] = useState('');
  const [warrantyType, setWarrantyType] = useState<WarrantyType>('NONE');
  const [warrantyNote, setWarrantyNote] = useState('');
  const [costLimit, setCostLimit] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [technicianId, setTechnicianId] = useState('');
  const [estimated, setEstimated] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [flatRateIds, setFlatRateIds] = useState<string[] | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const { data: customerDetail } = useQuery({
    queryKey: ['customers', customer?.id],
    queryFn: () => api<CustomerDetail>(`/customers/${customer!.id}`),
    enabled: Boolean(customer),
  });
  const { data: devices } = useQuery({
    queryKey: ['devices', 'customer', customer?.id],
    queryFn: () => api<Paginated<DeviceItem>>(`/devices?pageSize=50&customerId=${customer!.id}`),
    enabled: Boolean(customer),
  });
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), retry: false });
  const { data: rates } = useQuery({ queryKey: ['repair-flat-rates'], queryFn: () => api<{ items: FlatRate[] }>('/repair-flat-rates') });
  const activeRates = rates?.items.filter((r) => r.active) ?? [];
  // Vorauswahl folgt der Abrechnungsart, bis manuell gewählt wird (Garantie/Kulanz: keine Pauschalen)
  const chosenRates = flatRateIds ?? (warrantyType === 'NONE' ? activeRates.filter((r) => r.autoAdd).map((r) => r.id) : []);

  const addAccessory = (a: string) => {
    const v = a.trim();
    if (v && !accessories.includes(v)) setAccessories((x) => [...x, v]);
    setAccessoryInput('');
  };

  const costLimitCents = parseEuro(costLimit);
  const deviceValid = deviceId !== 'new' || (device.manufacturer.trim() && device.deviceType.trim() && device.model.trim());
  const valid = customer && deviceValid && fault.trim().length >= 3 && (costLimit.trim() === '' || costLimitCents !== null);

  const create = useMutation({
    mutationFn: async () => {
      const r = await api<{ id: string; number: string }>('/repairs', {
        method: 'POST',
        body: {
          customerId: customer!.id,
          contactId: contactId || null,
          ticketId,
          deviceId: deviceId === 'new' ? null : deviceId,
          device: deviceId === 'new' ? { ...device, serialNumber: device.serialNumber || null, inventoryNumber: device.inventoryNumber || null } : undefined,
          faultDescription: fault,
          condition: condition || null,
          accessories,
          warrantyType,
          warrantyNote: warrantyNote || null,
          costLimitCents,
          accessCode: accessCode || null,
          technicianId: technicianId || null,
          estimatedCompletionAt: estimated ? new Date(`${estimated}T17:00:00`).toISOString() : null,
          flatRateIds: rates ? chosenRates : undefined,
        },
      });
      for (const p of photos) {
        const form = new FormData();
        form.append('kind', 'INTAKE');
        form.append('customerVisible', 'true');
        form.append('file', p);
        try {
          await api(`/repairs/${r.id}/attachments`, { method: 'POST', form });
        } catch (e) {
          toast.error(`${p.name}: ${errorMessage(e)}`);
        }
      }
      return r;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['repairs'] });
      toast.success(`Gerät angenommen – ${r.number}`);
      navigate(`/repairs/${r.id}?new=1`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const setDev = (k: keyof typeof emptyDevice) => (e: { target: { value: string } }) => setDevice((d) => ({ ...d, [k]: e.target.value }));

  return (
    <>
      <PageHeader title="Gerät annehmen" description={ticketId ? 'Reparatur zum Ticket anlegen' : 'Reparaturnummer, Annahmeschein und Etikett werden automatisch erstellt.'} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
        className="grid max-w-5xl gap-4 lg:grid-cols-2"
      >
        <Card>
          <CardHeader>
            <CardTitle>Kunde</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <CustomerPicker value={customer} onChange={(c) => { setCustomer(c); setContactId(''); setDeviceId('new'); }} />
              </div>
              <Button type="button" variant="outline" onClick={() => setNewCustomerOpen(true)}>
                <Plus /> Neu
              </Button>
            </div>
            {customerDetail && customerDetail.contacts.length > 0 && (
              <Field label="Ansprechpartner" htmlFor="r-contact">
                <Select id="r-contact" value={contactId} onChange={(e) => setContactId(e.target.value)}>
                  <option value="">– keiner –</option>
                  {customerDetail.contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.firstName} {c.lastName}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {customerDetail && (
              <p className="text-xs text-muted-foreground">
                {[customerDetail.phone ?? customerDetail.mobile, customerDetail.email].filter(Boolean).join(' · ') || 'Keine Kontaktdaten – bitte für die Abholbenachrichtigung ergänzen.'}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gerät</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {devices && devices.items.length > 0 && (
              <Field label="Bekanntes Gerät" htmlFor="r-device" className="sm:col-span-2">
                <Select id="r-device" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                  <option value="new">+ Neues Gerät erfassen</option>
                  {devices.items.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.deviceType} {d.manufacturer} {d.model}
                      {d.serialNumber ? ` · SN ${d.serialNumber}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {deviceId === 'new' && (
              <>
                <Field label="Gerätetyp" htmlFor="r-type">
                  <Input id="r-type" list="device-types" value={device.deviceType} onChange={setDev('deviceType')} placeholder="z. B. Notebook" />
                  <datalist id="device-types">
                    {['Notebook', 'PC', 'Smartphone', 'Tablet', 'Monitor', 'Fernseher', 'Beamer', 'Drucker', 'Verstärker', 'Lautsprecher', 'Kamera', 'Konsole'].map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </Field>
                <Field label="Hersteller" htmlFor="r-man">
                  <Input id="r-man" value={device.manufacturer} onChange={setDev('manufacturer')} />
                </Field>
                <Field label="Modell" htmlFor="r-model">
                  <Input id="r-model" value={device.model} onChange={setDev('model')} />
                </Field>
                <Field label="Seriennummer" htmlFor="r-sn">
                  <Input id="r-sn" value={device.serialNumber} onChange={setDev('serialNumber')} className="font-mono" />
                </Field>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Fehler und Zustand</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Fehlerbeschreibung" htmlFor="r-fault" className="sm:col-span-2">
              <Textarea id="r-fault" rows={4} value={fault} onChange={(e) => setFault(e.target.value)} placeholder="Was ist defekt? Seit wann? Unter welchen Umständen?" />
            </Field>
            <Field label="Optischer Zustand" htmlFor="r-cond">
              <Textarea id="r-cond" rows={3} value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="Kratzer, Dellen, fehlende Teile …" />
            </Field>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Zubehör</span>
              <div className="flex gap-2">
                <Input
                  value={accessoryInput}
                  onChange={(e) => setAccessoryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAccessory(accessoryInput);
                    }
                  }}
                  placeholder="Eingeben und Enter"
                  aria-label="Zubehör"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {accessories.map((a) => (
                  <Badge key={a} className="gap-1">
                    {a}
                    <button type="button" onClick={() => setAccessories((x) => x.filter((y) => y !== a))} aria-label={`${a} entfernen`}>
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {ACCESSORY_SUGGESTIONS.filter((s) => !accessories.includes(s)).map((s) => (
                  <button key={s} type="button" onClick={() => addAccessory(s)} className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Abrechnung</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Field label="Garantie / Gewährleistung" htmlFor="r-war">
              <Select id="r-war" value={warrantyType} onChange={(e) => setWarrantyType(e.target.value as WarrantyType)}>
                {Object.entries(WARRANTIES).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            {warrantyType !== 'NONE' && (
              <Field label="Nachweis / Hinweis" htmlFor="r-warnote">
                <Input id="r-warnote" value={warrantyNote} onChange={(e) => setWarrantyNote(e.target.value)} placeholder="z. B. Kaufbeleg vom 12.03.2025" />
              </Field>
            )}
            <Field
              label="Kostenfreigabe bis (€, brutto)"
              htmlFor="r-limit"
              hint="Bis zu diesem Betrag darf ohne Rückfrage repariert werden. Leer = immer Kostenvoranschlag."
              error={costLimit.trim() && costLimitCents === null ? 'Ungültiger Betrag' : undefined}
            >
              <Input id="r-limit" inputMode="decimal" value={costLimit} onChange={(e) => setCostLimit(e.target.value)} placeholder="z. B. 150,00" />
            </Field>
            {activeRates.length > 0 && (
              <fieldset className="grid gap-1.5">
                <legend className="mb-1 text-sm font-medium">Pauschalen</legend>
                {activeRates.map((r) => (
                  <label key={r.id} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      className="mt-0.5"
                      checked={chosenRates.includes(r.id)}
                      onCheckedChange={(v) => setFlatRateIds(v ? [...chosenRates, r.id] : chosenRates.filter((x) => x !== r.id))}
                    />
                    <span>
                      {r.name} – {formatCents(r.priceCents)} netto
                      {r.creditOnRepair && <span className="block text-xs text-muted-foreground">wird bei Durchführung der Reparatur verrechnet</span>}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Werkstatt</CardTitle>
            <CardDescription>Der Gerätecode wird verschlüsselt gespeichert und nie gedruckt oder Kunden angezeigt.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Gerätecode / Passwort" htmlFor="r-code" className="sm:col-span-2">
              <Input id="r-code" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Techniker" htmlFor="r-tech">
              <Select id="r-tech" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
                <option value="">– später –</option>
                {meta?.agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Voraussichtlich fertig" htmlFor="r-est">
              <Input id="r-est" type="date" value={estimated} onChange={(e) => setEstimated(e.target.value)} />
            </Field>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Fotos bei Annahme</CardTitle>
            <CardDescription>Dokumentieren Sie den Zustand des Geräts (auch direkt mit der Smartphone-Kamera).</CardDescription>
          </CardHeader>
          <CardContent>
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                setPhotos((p) => [...p, ...files]);
                e.target.value = '';
              }}
            />
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative size-20 overflow-hidden rounded-md border">
                  <img src={URL.createObjectURL(p)} alt="" className="size-full object-cover" />
                  <button
                    type="button"
                    className="absolute right-0.5 top-0.5 rounded bg-background/80 p-0.5"
                    onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))}
                    aria-label="Foto entfernen"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => photoInput.current?.click()}
                className={cn('flex size-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed text-xs text-muted-foreground hover:bg-accent')}
              >
                <Camera className="size-5" /> Foto
              </button>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!valid} loading={create.isPending}>
              Gerät annehmen
            </Button>
          </CardFooter>
        </Card>
      </form>
      <CustomerDialog
        customer={null}
        open={newCustomerOpen}
        onOpenChange={setNewCustomerOpen}
        onSaved={(c) => {
          qc.invalidateQueries({ queryKey: ['customers'] });
          setCustomer({ id: c.id, name: c.name });
          setDeviceId('new');
        }}
      />
    </>
  );
}
